import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/dashboard.css";

import Overview     from "./Overview";
import Reservations from "./Reservations";
import CheckInOut   from "./CheckInOut";
import Rooms        from "./Rooms";
import Guests       from "./Guests";
import Billing      from "./Billing";
import Housekeeping from "./Housekeeping";
import Inventory    from "./Inventory";
import Staff        from "./Staff";
import Reports      from "./Reports";
import Messages     from "./Messages";
import UsersAccess  from "./UsersAccess";
import Settings     from "./Settings";
import { INVENTORY } from "../../data/mockData";
import { useFacility, splitFacilityName } from "../../context/FacilityContext";

// Each nav item carries the permission key the user must have to see it.
// Permissions live in the DB (`permissions` table + role_permissions
// junction) and are returned with the login response.  `perm: null` means
// "always visible" — used for Overview, which is the home screen.
//
// Super-admin holds `system.full_access` and bypasses every check (see
// `canSee` below), so they always see every tab regardless of grants.
const NAV_ITEMS = [
  { id:"overview",     icon:"⊞",  label:"Overview",       perm: null },
  { id:"bookings",     icon:"📋", label:"Reservations",   perm: "reservations.manage" },
  { id:"checkinout",   icon:"🔑", label:"Check In / Out", perm: "checkinout.manage" },
  { id:"rooms",        icon:"🏨", label:"Rooms",          perm: "rooms.view" },
  { id:"guests",       icon:"👥", label:"Guests",         perm: "guests.manage" },
  { id:"billing",      icon:"💳", label:"Billing",        perm: "billing.manage" },
  { id:"housekeeping", icon:"🧹", label:"Housekeeping",   perm: "housekeeping.manage" },
  { id:"inventory",    icon:"📦", label:"Inventory",      perm: "modules.all" },
  { id:"staff",        icon:"👨‍💼", label:"Staff",          perm: "users.manage" },
  { id:"messages",     icon:"💬", label:"Messages",       perm: "modules.all" },
  { id:"reports",      icon:"📊", label:"Reports",        perm: "reports.view" },
];

const NOTIFICATIONS = [
  { type:"⚠️", text:"Room 104 maintenance overdue" },
  { type:"📦", text:"Inventory: Toilet paper critically low" },
  { type:"📦", text:"Inventory: Bed Sheets running low" },
  { type:"🔑", text:"Room 106 — checkout due today" },
  { type:"📋", text:"BK002 awaiting confirmation" },
];

export default function ManagementDashboard() {
  const user    = JSON.parse(localStorage.getItem("user") || "null");
  // Only the super_admin can create / manage users (matches AdminRoute and
  // the backend's requireSuperAdmin middleware).
  const isAdmin = user?.role === "super_admin";
  const navigate = useNavigate();
  const { facility } = useFacility();
  const [firstWord, restName] = splitFacilityName(facility?.name);

  const [activeTab,   setActiveTab]   = useState("overview");
  const [notifOpen,   setNotifOpen]   = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Permission gate.  The /login response now returns the user's permission
  // keys, so we can hide nav items a role wasn't granted.  Super-admin
  // bypasses everything via `system.full_access`.  Older logged-in sessions
  // (cached before this change) won't have `permissions` on the user
  // object — fall back to "show everything" so we don't lock them out
  // mid-session; once they log out and back in, the gate kicks in.
  const perms = new Set(Array.isArray(user?.permissions) ? user.permissions : []);
  const isLegacySession = !Array.isArray(user?.permissions);
  const hasFullAccess   = isAdmin || perms.has("system.full_access");
  const canSee = (perm) =>
    !perm || hasFullAccess || isLegacySession || perms.has(perm);

  const adminExtras = [
    { id:"users",    icon:"👥", label:"Users & Access", perm: "users.manage" },
    { id:"settings", icon:"⚙️", label:"Settings",       perm: "system.settings" },
  ];
  const navItems = [...NAV_ITEMS, ...(isAdmin ? adminExtras : [])]
    .filter(item => canSee(item.perm));

  const alerts = INVENTORY.filter(i => i.status !== "ok").length;

  const logout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  // Guard against rendering a tab the user doesn't have permission for —
  // e.g. an active tab cached in component state across a role change,
  // or future deep-linking by id.  Anything denied falls back to Overview.
  const allowedIds = new Set(navItems.map(n => n.id));
  const renderPage = () => {
    if (!allowedIds.has(activeTab)) {
      return <Overview isAdmin={isAdmin} setActiveTab={setActiveTab} />;
    }
    switch (activeTab) {
      case "overview":     return <Overview     isAdmin={isAdmin} setActiveTab={setActiveTab} />;
      case "bookings":     return <Reservations />;
      case "checkinout":   return <CheckInOut />;
      case "rooms":        return <Rooms />;
      case "guests":       return <Guests />;
      case "billing":      return <Billing />;
      case "housekeeping": return <Housekeeping />;
      case "inventory":    return <Inventory />;
      case "staff":        return <Staff />;
      case "messages":     return <Messages />;
      case "reports":      return <Reports />;
      case "users":        return <UsersAccess />;
      case "settings":     return <Settings />;
      default:             return <Overview isAdmin={isAdmin} setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="d-root">

      {/* ── SIDEBAR ── */}
      <aside className={`d-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="s-logo">
          <div className="s-logo-name">
            {firstWord ? <em>{firstWord}</em> : <em>Hotel</em>}
            {restName ? ` ${restName}` : null}
          </div>
          <div className="s-logo-sub">
            {facility?.location || "Management System"}
          </div>
        </div>

        <div className="s-user">
          <div className="s-user-name">{user?.full_name || "Staff"}</div>
          <div className="s-user-role">{user?.role || "user"}</div>
        </div>

        <div className="s-section-label">Navigation</div>
        <nav className="s-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`s-nav-item ${activeTab === item.id ? "active" : ""}`}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
            >
              <span className="s-nav-icon">{item.icon}</span>
              {item.label}
              {item.id === "inventory" && alerts > 0 && (
                <span className="s-alert-badge">{alerts}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="s-footer">
          <button className="s-logout" onClick={logout}>Sign Out</button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <main className="d-main">

        {/* TOPBAR */}
        <div className="d-topbar">
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{ display:"none", background:"none", border:"none", cursor:"pointer", fontSize:20, color:"var(--dark)" }}
              className="hamburger-btn"
            >☰</button>
            <div className="d-topbar-title">
              {navItems.find(n => n.id === activeTab)?.label || "Dashboard"}
            </div>
          </div>

          <div className="d-topbar-right">
            <div className="d-topbar-date">
              {new Date().toLocaleDateString("en-GB", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
            </div>

            <div style={{ position:"relative" }}>
              <button className="notif-btn" onClick={() => setNotifOpen(!notifOpen)}>
                🔔<span className="notif-dot" />
              </button>
              {notifOpen && (
                <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, width:290, background:"#fff", borderRadius:10, border:"1px solid var(--border)", boxShadow:"0 8px 32px rgba(40,20,10,.12)", zIndex:200, overflow:"hidden", animation:"fadeUp .2s both" }}>
                  <div style={{ padding:"10px 16px", borderBottom:"1px solid var(--border)", fontSize:11, letterSpacing:".1em", textTransform:"uppercase", color:"var(--mid)" }}>
                    Notifications
                  </div>
                  {NOTIFICATIONS.map((n, i) => (
                    <div key={i} style={{ display:"flex", gap:10, padding:"10px 16px", borderBottom:"1px solid rgba(180,140,80,.06)", fontSize:12, color:"#4a3828", fontWeight:300 }}>
                      <span>{n.type}</span>{n.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PAGE CONTENT */}
        <div className="d-content" onClick={() => notifOpen && setNotifOpen(false)}>
          {renderPage()}
        </div>

      </main>
    </div>
  );
}
